"use client"

import { useState, useEffect, useCallback } from "react"
import Image from "@/src/components/Image"
import { Button } from "@/components/ui/button"
import { DashboardLayout } from "@/components/dashboard/dashboard-layout"
import { ConnectionForm } from "./components/connection-form"
import { lineChannelsAPI, linkedAccountsAPI } from "@/src/lib/api/line-channels"
import { lineGroupsAPI } from "@/src/lib/api/line-groups"

type ConnectionType = "line-oa" | "line-chat" | "line-group" | null

export default function ConnectsPage() {
  const [selectedConnection, setSelectedConnection] = useState<ConnectionType>(null)
  const [isLineOAConnected, setIsLineOAConnected] = useState(false)
  const [isLineChatConnected, setIsLineChatConnected] = useState(false)
  const [isLineGroupConnected, setIsLineGroupConnected] = useState(false)

  const refreshStatus = useCallback(async () => {
    try {
      console.log('[refreshStatus] fetching LINE OA channels...')
      const channels = await lineChannelsAPI.getAll()
      console.log('[refreshStatus] channels:', channels)
      setIsLineOAConnected(channels.some((c) => c.is_active))
    } catch (err) {
      console.error('[refreshStatus] ERROR:', err)
    }
  }, [])

  const refreshLineChatStatus = useCallback(async () => {
    try {
      console.log('[refreshLineChatStatus] calling isSharedLineLinked...')
      const linked = await linkedAccountsAPI.isSharedLineLinked()
      console.log('[refreshLineChatStatus] result:', linked)
      setIsLineChatConnected(linked)
    } catch (err) {
      console.error('[refreshLineChatStatus] ERROR:', err)
    }
  }, [])

  const refreshGroupStatus = useCallback(async () => {
    try {
      const groups = await lineGroupsAPI.getAll()
      setIsLineGroupConnected(groups.length > 0)
    } catch (err) {
      console.error('[refreshGroupStatus] ERROR:', err)
    }
  }, [])

  useEffect(() => {
    refreshStatus()
    refreshLineChatStatus()
    refreshGroupStatus()
  }, [refreshStatus, refreshLineChatStatus, refreshGroupStatus])

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4 lg:mb-6">
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-4">การเชื่อมต่อ</h1>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-lg shadow-sm">
          <h2 className="text-lg lg:text-xl font-semibold text-gray-900 p-6">เลือกวิธีการตรวจสอบสลิป</h2>
          <hr className="border-gray-200" />

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr]">
            {/* Left Section - Connection Options */}
            <div className="p-6">
              <div className="space-y-4">
                {/* LINE Official Account Card */}
                <div
                  className="bg-gray-50 rounded-lg p-6 flex gap-4 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => setSelectedConnection("line-oa")}
                >
                  <div className="flex-shrink-0">
                    <Image
                      src="/icons/icon-line-oa.svg"
                      alt="LINE Official Account"
                      width={60}
                      height={60}
                      className="w-12 h-12 lg:w-15 lg:h-15"
                      style={{
                        filter: "invert(64%) sepia(88%) saturate(364%) hue-rotate(75deg) brightness(95%) contrast(85%)",
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-base lg:text-lg font-semibold text-brand-primary">LINE Official Account</h3>
                      <Button
                        size="sm"
                        className={`${isLineOAConnected ? "bg-brand-primary hover:bg-brand-primary/90" : "bg-brand-red hover:bg-brand-red/90"} text-white rounded-lg px-4 py-1 text-sm whitespace-nowrap`}
                      >
                        {isLineOAConnected ? "เชื่อมต่อแล้ว" : "ยังไม่ได้เชื่อมต่อ"}
                      </Button>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      เชื่อมต่อ LINE OA ของคุณกับ <span className="text-brand-red font-semibold">THAISLIPCHECK</span>
                    </p>
                    <p className="text-sm text-gray-700 leading-relaxed">เมื่อลูกค้าส่ง</p>
                    <p className="text-sm text-gray-700 leading-relaxed">สลิปเข้ามา ระบบจะตรวจสอบให้โดยอัตโนมัติ</p>
                  </div>
                </div>

                {/* LINE THAISLIPCHECK Card */}
                <div
                  className="bg-gray-50 rounded-lg p-6 flex gap-4 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => setSelectedConnection("line-chat")}
                >
                  <div className="flex-shrink-0">
                    <Image
                      src="/icons/icon-line.svg"
                      alt="LINE THAISLIPCHECK"
                      width={60}
                      height={60}
                      className="w-12 h-12 lg:w-15 lg:h-15"
                      style={{
                        filter: "invert(64%) sepia(88%) saturate(364%) hue-rotate(75deg) brightness(95%) contrast(85%)",
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-base lg:text-lg font-semibold text-brand-primary">LINE THAISLIPCHECK</h3>
                      <Button
                        size="sm"
                        className={`${isLineChatConnected ? "bg-brand-primary hover:bg-brand-primary/90" : "bg-brand-red hover:bg-brand-red/90"} text-white rounded-lg px-4 py-1 text-sm whitespace-nowrap`}
                      >
                        {isLineChatConnected ? "เชื่อมต่อแล้ว" : "ยังไม่ได้เชื่อมต่อ"}
                      </Button>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      เพียงแอด LINE <span className="text-brand-red font-semibold">THAISLIPCHECK</span> แล้วส่งสลิปให้เรา
                    </p>
                    <p className="text-sm text-gray-700 leading-relaxed">ตรวจสอบให้</p>
                  </div>
                </div>

                {/* LINE Group Chat Card */}
                <div
                  className="bg-gray-50 rounded-lg p-6 flex gap-4 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => setSelectedConnection("line-group")}
                >
                  <div className="flex-shrink-0">
                    <Image
                      src="/icons/icon-line-chat.svg"
                      alt="LINE กลุ่มแชท"
                      width={60}
                      height={60}
                      className="w-12 h-12 lg:w-15 lg:h-15"
                      style={{
                        filter: "invert(64%) sepia(88%) saturate(364%) hue-rotate(75deg) brightness(95%) contrast(85%)",
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-base lg:text-lg font-semibold text-brand-primary">LINE กลุ่มแชท</h3>
                      <Button
                        size="sm"
                        className={`${isLineGroupConnected ? "bg-brand-primary hover:bg-brand-primary/90" : "bg-brand-red hover:bg-brand-red/90"} text-white rounded-lg px-4 py-1 text-sm whitespace-nowrap`}
                      >
                        {isLineGroupConnected ? "เชื่อมต่อแล้ว" : "ยังไม่ได้เชื่อมต่อ"}
                      </Button>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      เพียงเพิ่ม LINE <span className="text-brand-red font-semibold">THAISLIPCHECK</span>
                    </p>
                    <p className="text-sm text-gray-700 leading-relaxed">ลงในกลุ่มของคุณแล้วส่ง</p>
                    <p className="text-sm text-gray-700 leading-relaxed">สลิปให้เรา ตรวจสอบได้เลย</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="hidden lg:block border-l border-gray-200"></div>

            {/* Right Section - Form or Empty State */}
            <div className="hidden lg:block">
              {selectedConnection ? (
                <ConnectionForm
                  selectedType={selectedConnection}
                  onClose={() => setSelectedConnection(null)}
                  onChannelsChange={() => { refreshStatus(); refreshLineChatStatus(); refreshGroupStatus() }}
                  isLineChatConnected={isLineChatConnected}
                />
              ) : (
                <div className="p-6 flex flex-col items-center justify-center h-full">
                  <Image
                    src="/icons/icon-connects.svg"
                    alt="Connection icon"
                    width={192}
                    height={192}
                    className="w-48 h-48 text-gray-300 mb-4 opacity-20"
                  />
                  <p className="text-xl text-gray-300 font-medium text-center">เลือกวิธีการตรวจสอบสลิป</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
